import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { reportProps } from './StopLossAggregateTable';
import React, { useEffect, useState } from 'react';
import {
  Add,
  Agreement,
  BlankCard,
  CardComponent,
  CustomerService2,
  Equal,
  Folder3,
  Folder4,
  LifeInsurance,
  Meeting,
  Message,
  MessageSolid,
  Nurse2,
  Pharmacist2,
  Phone,
  PieChartWithoutCard,
  SaveMoney,
  VerticalLabelValue,
  VideoCamera,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import CareNavigatorConstants from '../core-utils/messages/CareNavigatorConstants';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';
import { commasForNumbers } from '../core-utils/helper-utils/helper';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';

const CareNavigationOverview: React.FC<reportProps> = ({ data }) => {
  const messages = CareNavigatorConstants.QUARTERLY_REPORT;
  const iconStyle = { height: '18px', width: '18px' };
  const useStyles = makeStyles()({
    container: {
      marginTop: '20px',
      gap: '20px',
    },
    innerContainer: {
      width: '68%',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
  });
  const { classes } = useStyles();
  const [overview, setOverview] = useState<any>();

  const formatData = async (data: any) => {
    const colors = ['#FFC354', patientAccessTheme.palette.primary[500]];

    // Process openCases: Only apply top 2 logic if more than 3 items exist
    const sortedCases = [...(data?.openCases || [])].sort(
      (a, b) => b.count - a.count,
    );
    let formattedOpenCases;

    if (sortedCases.length > 4) {
      const topCases = sortedCases.slice(0, 3);
      const remainingCases = sortedCases.slice(3);
      const othersCount = remainingCases.reduce(
        (sum: number, item) => sum + item.count,
        0,
      );

      formattedOpenCases = topCases.map((item) => ({
        name: item._id,
        value: item.count.toString(),
      }));

      const othersEntry = formattedOpenCases.find(
        (item) => item.name === 'Others',
      );
      if (othersEntry) {
        othersEntry.value = (
          parseInt(othersEntry.value) + othersCount
        ).toString();
      } else if (othersCount > 0) {
        formattedOpenCases.push({
          name: 'Others',
          value: othersCount.toString(),
        });
      }
    } else {
      formattedOpenCases = sortedCases.map((item) => ({
        name: item._id,
        value: item.count.toString(),
      }));
    }
    // Process caseSurveys: Merge 'No Case Type' first, then apply top 3 logic if more than 4 items exist
    let caseSurveys = [...(data?.caseSurveys?.caseTypes || [])];
    let othersCaseSurveyCount = 0;

    // Merge 'No Case Type' with 'Others'
    caseSurveys = caseSurveys.filter((item) => {
      if (item.type === 'No Case Type') {
        othersCaseSurveyCount += item.count;
        return false;
      }
      return true;
    });

    let formattedCaseSurveys;
    if (caseSurveys.length > 4) {
      const sortedCaseSurveys = caseSurveys.sort((a, b) => b.count - a.count);
      const topCaseSurveys = sortedCaseSurveys.slice(0, 3);
      const remainingCaseSurveys = sortedCaseSurveys.slice(3);

      othersCaseSurveyCount += remainingCaseSurveys.reduce(
        (sum: number, item) => sum + item.count,
        0,
      );

      formattedCaseSurveys = topCaseSurveys.map((item) => ({
        name: item.type,
        value: item.count.toString(),
      }));
    } else {
      formattedCaseSurveys = caseSurveys.map((item) => ({
        name: item.type,
        value: item.count.toString(),
      }));
    }
    const othersSurveyEntry = formattedCaseSurveys.find(
      (item) => item.name === 'Others',
    );
    if (othersSurveyEntry) {
      othersSurveyEntry.value = (
        parseInt(othersSurveyEntry.value) + othersCaseSurveyCount
      ).toString();
    } else if (othersCaseSurveyCount > 0) {
      formattedCaseSurveys.push({
        name: 'Others',
        value: othersCaseSurveyCount.toString(),
      });
    }
    const updatedData = {
      openCases: {
        total: sortedCases
          .reduce((sum: number, item) => sum + item.count, 0)
          .toString(),
        data: formattedOpenCases,
      },
      engagements: {
        total:
          data?.engagements?.total?.toString() ||
          data?.engagements?.data
            ?.reduce((sum: number, item: any) => sum + item?.count, 0)
            ?.toString(),
        data: data?.engagements?.data?.reduce((acc: any, item: any) => {
          acc[item.mode] = item?.count?.toString();
          return acc;
        }, {}),
      },
      careNavigators: {
        total: data?.careNavigators
          ?.reduce((sum: number, item: any) => sum + item?.count, 0)
          ?.toString(),
        data: data?.careNavigators?.reduce((acc: any, item: any) => {
          acc[item?._id] = item?.count?.toString();
          return acc;
        }, {}),
      },
      caseSurveys: {
        total: data?.caseSurveys?.totalClosedCases?.toString(),
        savings: `$${
          commasForNumbers(data?.caseSurveys?.savingsTotal, 0) ?? 0
        }`,
        data: formattedCaseSurveys,
      },
      engagementByTypes: {
        data: data?.engagementByTypes?.map((item: any, index: number) => ({
          label: item?._id,
          value: item.count,
          color: colors[index % colors.length],
        })),
      },
    };
    setOverview(updatedData);
  };

  const getLegend = (item: any) => {
    return (
      <Grid
        style={{
          display: 'flex',
          alignItems: 'center',
          margin: '0px 24px 0px 0px',
        }}
      >
        <Grid
          style={{
            backgroundColor: item.color,
            height: '8px',
            width: '8px',
            borderRadius: '2px',
            marginRight: '8px',
          }}
        ></Grid>
        <Grid>
          <Box>
            <Typography
              style={{
                fontSize: '12px',
                lineHeight: '24px',
                fontWeight: 'normal',
                color: patientAccessTheme.palette.text.mediumemphasis,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  };
  useEffect(() => {
    formatData(data?.careNavigation);
  }, []);

  return (
    <>
      <Grid container className={classes.container}>
        <Grid className={classes.innerContainer}>
          <BlankCard
            mainGridStyles={{ padding: '0px 24px' }}
            body={
              <Grid
                container
                style={{ gap: '10px' }}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Agreement style={{ width: '60' }} />
                <VerticalLabelValue
                  title={messages.engagements}
                  value={overview?.engagements?.total}
                  rootStyles={{ width: '171px', padding: '16px 0px' }}
                />
                {overview?.engagements?.total !== '0' && (
                  <>
                    <Equal style={iconStyle} />
                    {[
                      {
                        key: 'On Call',
                        title: messages.phoneCalls,
                        icon: <Phone />,
                      },
                      {
                        key: 'On Text',
                        title: messages.textMessage,
                        icon: <MessageSolid />,
                      },
                      {
                        key: 'Emails',
                        title: messages.email,
                        icon: <Message />,
                      },
                      {
                        key: 'Video Call',
                        title: messages.videoCalls,
                        icon: <VideoCamera />,
                      },
                      {
                        key: 'In Person',
                        title: messages.inPerson,
                        icon: <Meeting />,
                      },
                    ]
                      .filter(
                        ({ key }) =>
                          Number(overview?.engagements?.data[key]) > 0,
                      )
                      .map(({ key, title, icon }, index, arr) => (
                        <React.Fragment key={key}>
                          <VerticalLabelValue
                            title={title}
                            value={overview?.engagements?.data[key]}
                            icon={icon}
                            rootStyles={{ padding: '16px 0px' }}
                          />
                          {index < arr.length - 1 && (
                            <Add
                              stroke={patientAccessTheme.palette.status.icon}
                              fill={patientAccessTheme.palette.status.icon}
                              style={iconStyle}
                            />
                          )}
                        </React.Fragment>
                      ))}
                  </>
                )}
              </Grid>
            }
          />
          <BlankCard
            mainGridStyles={{ padding: '0px 24px' }}
            body={
              <Grid
                container
                style={{ gap: '10px' }}
                sx={{
                  alignItems: 'center',
                }}
              >
                <LifeInsurance />
                <VerticalLabelValue
                  title={messages.careNavigator}
                  value={overview?.careNavigators?.total}
                  rootStyles={{ width: '171px', padding: '16px 0px' }}
                />
                {overview?.careNavigators?.total !== '0' && (
                  <>
                    <Equal style={iconStyle} />
                    {[
                      {
                        key: 'care_navigator_nurse',
                        title: messages.nurse,
                        icon: <Nurse2 />,
                      },
                      {
                        key: 'care_navigator_pharmacist',
                        title: messages.pharmacist,
                        icon: <Pharmacist2 />,
                      },
                      {
                        key: 'care_navigator_customer_service',
                        title: messages.customerService,
                        icon: <CustomerService2 />,
                      },
                    ]
                      .filter(
                        ({ key }) =>
                          Number(overview?.careNavigators?.data?.[key]) > 0,
                      )
                      .map(({ key, title, icon }, index, arr) => (
                        <React.Fragment key={key}>
                          <VerticalLabelValue
                            title={title}
                            value={overview?.careNavigators?.data?.[key]}
                            icon={icon}
                            rootStyles={{ padding: '16px 0px' }}
                          />
                          {index < arr.length - 1 && (
                            <Add
                              stroke={patientAccessTheme.palette.status.icon}
                              fill={patientAccessTheme.palette.status.icon}
                              style={iconStyle}
                            />
                          )}
                        </React.Fragment>
                      ))}
                  </>
                )}
              </Grid>
            }
          />
          <BlankCard
            mainGridStyles={{ padding: '0px 24px' }}
            body={
              <Grid
                container
                style={{ gap: '10px' }}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Folder3 />
                <VerticalLabelValue
                  title={messages.caseOpened}
                  value={overview?.openCases?.total}
                  rootStyles={{ width: '171px', padding: '16px 0px' }}
                />
                {overview?.openCases?.total !== '0' && (
                  <>
                    <Equal style={iconStyle} />
                    {overview?.openCases?.data?.map(
                      (item: any, index: number) => (
                        <>
                          {index !== 0 && (
                            <Add
                              stroke={patientAccessTheme.palette.status.icon}
                              fill={patientAccessTheme.palette.status.icon}
                              style={iconStyle}
                            />
                          )}
                          <VerticalLabelValue
                            title={item?.name}
                            value={item?.value}
                            rootStyles={{ padding: '16px 0px' }}
                          />
                        </>
                      ),
                    )}
                  </>
                )}
              </Grid>
            }
          />
          <BlankCard
            mainGridStyles={{ padding: '0px 24px' }}
            body={
              <Grid
                container
                style={{ gap: '10px' }}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Folder4 />
                <VerticalLabelValue
                  title={messages.caseClosed}
                  value={overview?.caseSurveys?.total}
                  rootStyles={{ width: '171px', padding: '16px 0px' }}
                />
                {overview?.caseSurveys?.total !== '0' && (
                  <>
                    <Equal style={iconStyle} />
                    {overview?.caseSurveys?.data?.map(
                      (item: any, index: number) => (
                        <>
                          {index !== 0 && (
                            <Add
                              stroke={patientAccessTheme.palette.status.icon}
                              fill={patientAccessTheme.palette.status.icon}
                              style={iconStyle}
                            />
                          )}
                          <VerticalLabelValue
                            title={item?.name}
                            value={item?.value}
                            rootStyles={{ padding: '16px 0px' }}
                          />
                        </>
                      ),
                    )}
                  </>
                )}
              </Grid>
            }
          />
        </Grid>
        <Grid className={classes.innerContainer} style={{ width: '30%' }}>
          <BlankCard
            mainGridStyles={{ padding: '0px 24px' }}
            body={
              <Grid
                container
                style={{ gap: '10px' }}
                sx={{
                  alignItems: 'center',
                }}
              >
                <SaveMoney style={{ width: '60' }} />
                <VerticalLabelValue
                  title={messages.overAllSavings}
                  value={overview?.caseSurveys?.savings}
                  titleColor={patientAccessTheme.palette.status.white}
                  valueColor={patientAccessTheme.palette.status.white}
                />
              </Grid>
            }
            showBackGroundImage={true}
          />
          <CardComponent
            title={messages.pieChart}
            height="354px"
            width="100%"
            body={
              <>
                <Grid style={{ display: 'flex' }}>
                  <PieChartWithoutCard
                    data={overview?.engagementByTypes?.data}
                    showLegend={false}
                    showValues={true}
                  />
                </Grid>
                <Grid
                  container
                  style={{
                    justifyContent: 'center',
                    margin: '10px 0px 0px 25px',
                  }}
                >
                  {overview?.engagementByTypes?.data?.map((item: any) =>
                    getLegend(item),
                  )}
                </Grid>
              </>
            }
          />
        </Grid>
      </Grid>
    </>
  );
};
export default CareNavigationOverview;
